import { useState } from 'react';
import type { Invitacion } from '@eventos-cien/shared';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { useDispositivoTactil } from '../lib/useDispositivoTactil';
import { marcarAsistencia } from './marcarAsistencia';

interface Props {
  eventoId: string;
  invitacion: Invitacion;
  mesa: { numero: number; nombre: string } | null;
  onCerrar: () => void;
}

export function TarjetaAsistencia({ eventoId, invitacion, mesa, onCerrar }: Props) {
  const { user } = useAuth();
  const tactil = useDispositivoTactil();
  const [marcando, setMarcando] = useState<'titular' | 'acompanante' | null>(null);
  const [flash, setFlash] = useState<'verde' | 'rojo' | null>(null);

  if (invitacion.estado === 'anulada') {
    return (
      <div className="w-full space-y-3">
        <BloqueAlto tono="rojo" activo={tactil}>
          Esta invitación fue anulada. No debe entrar.
        </BloqueAlto>
        <Button bloque grande variante="secundario" onClick={onCerrar}>
          Escanear otro
        </Button>
      </div>
    );
  }

  if (invitacion.estado !== 'registrada') {
    return (
      <div className="w-full space-y-3">
        <Callout tono="alerta">Este boleto todavía no se ha registrado.</Callout>
        <Button bloque grande variante="secundario" onClick={onCerrar}>
          Escanear otro
        </Button>
      </div>
    );
  }

  async function marcar(quien: 'titular' | 'acompanante', presente: boolean) {
    setMarcando(quien);
    try {
      await marcarAsistencia(eventoId, invitacion.token, quien, presente, user?.uid ?? null);
      if (presente && tactil) {
        if (navigator.vibrate) navigator.vibrate(120);
        setFlash('verde');
        setTimeout(() => setFlash(null), 700);
      }
    } finally {
      setMarcando(null);
    }
  }

  const a = invitacion.asistencia;

  return (
    <div className="w-full max-w-full space-y-3">
      {flash && <FlashPantallaCompleta tono={flash} />}

      <div className="flex w-full items-center justify-between gap-2">
        <strong className="truncate text-base text-texto sm:text-lg">{invitacion.invitado.nombre}</strong>
        {mesa && (
          <span className="shrink-0 rounded-full bg-navy/5 px-2.5 py-1 text-xs font-medium text-navy sm:px-3 sm:text-sm">
            Mesa {mesa.numero}
          </span>
        )}
      </div>

      <Casilla
        etiqueta="Titular"
        presente={a.titular.presente}
        cargando={marcando === 'titular'}
        grande={tactil}
        onToggle={() => void marcar('titular', !a.titular.presente)}
      />

      {invitacion.invitado.acompanante && (
        <Casilla
          etiqueta={invitacion.invitado.acompanante}
          presente={a.acompanante.presente}
          cargando={marcando === 'acompanante'}
          grande={tactil}
          onToggle={() => void marcar('acompanante', !a.acompanante.presente)}
        />
      )}

      <Button bloque grande variante="secundario" onClick={onCerrar}>
        Escanear otro
      </Button>
    </div>
  );
}

function BloqueAlto({
  tono,
  activo,
  children,
}: {
  tono: 'rojo' | 'verde';
  activo: boolean;
  children: string;
}) {
  if (!activo) return <Callout tono={tono === 'rojo' ? 'error' : 'exito'}>{children}</Callout>;
  return (
    <div
      className={[
        'flex min-h-36 w-full items-center justify-center rounded-2xl p-5 text-center text-lg font-semibold leading-snug text-white sm:min-h-40 sm:p-6 sm:text-xl',
        tono === 'rojo' ? 'bg-red-600' : 'bg-emerald-600',
      ].join(' ')}
      role="alert"
    >
      {children}
    </div>
  );
}

function FlashPantallaCompleta({ tono }: { tono: 'verde' | 'rojo' }) {
  return (
    <div
      className={[
        'pointer-events-none fixed inset-0 z-50 flex items-center justify-center',
        tono === 'verde' ? 'bg-emerald-600' : 'bg-red-600',
      ].join(' ')}
      style={{ animation: 'desvanecer 700ms ease-out forwards' }}
      aria-hidden="true"
    >
      <span className="text-6xl">{tono === 'verde' ? '✓' : '✕'}</span>
      <style>{'@keyframes desvanecer { 0% { opacity: 0.92 } 100% { opacity: 0 } }'}</style>
    </div>
  );
}

function Casilla({
  etiqueta,
  presente,
  cargando,
  grande,
  onToggle,
}: {
  etiqueta: string;
  presente: boolean;
  cargando: boolean;
  grande?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={cargando}
      className={[
        'flex w-full items-center justify-between gap-2 rounded-2xl border text-left transition active:scale-[0.98] disabled:cursor-not-allowed',
        grande ? 'p-5 sm:p-7' : 'p-4 sm:p-5',
        presente ? 'border-emerald-300 bg-emerald-50' : 'border-borde bg-white',
      ].join(' ')}
    >
      <span className={['min-w-0 truncate font-medium text-texto', grande ? 'text-base sm:text-lg' : 'text-sm sm:text-base'].join(' ')}>
        {etiqueta}
      </span>
      <span
        className={[
          'shrink-0 font-semibold',
          grande ? 'text-sm sm:text-base' : 'text-xs sm:text-sm',
          presente ? 'text-emerald-700' : 'text-texto/50',
        ].join(' ')}
      >
        {cargando ? '…' : presente ? 'Entró ✓' : 'Marcar entrada'}
      </span>
    </button>
  );
}
